import { useRef, type ReactNode } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import Markdown from 'react-markdown'
import { Check } from 'lucide-react'
import { Button, LinkButton } from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import {
  sectionVariants,
  slideFromLeftVariants,
  slideFromRightVariants,
  listItemVariants,
  reducedMotionVariants,
} from '../../lib/motion'
import { CodeWindowHero } from '../code-window'

export interface FeaturesSplitProps {
  /**
   * Media position on desktop.
   * - `'right'` (default): content left, image/code right.
   * - `'left'`: image/code left, content right.
   * @default 'right'
   */
  position?: 'left' | 'right'
  heading?: string
  description?: string
  /** Feature list with checkmarks — recommended: exactly 3 items */
  features?: Array<{
    icon?: ReactNode
    text: string
  }>
  /** Secondary button below the feature list */
  button?: { text: string; url: string }
  /** Optional ghost "Learn more" link below features or buttons */
  learnMore?: { text: string; url: string }
  image?: { src: string; alt: string }
  mediaType?: 'image' | 'code'
  codeWindow?: { code: string; language: string; filename?: string }
  className?: string
}

/**
 * FeaturesSplit - Two-column feature section with media on one side.
 *
 * One prop-driven component covering both split directions (formerly FeaturesSplit-right /
 * FeaturesSplit-left): `position="right"` puts the media on the right (content left),
 * `position="left"` mirrors it. The content always stacks above the media on
 * mobile; the columns swap on desktop.
 */
const FeaturesSplit = ({
  position = 'right',
  heading = 'Build Beautiful Interfaces with MDXUI',
  description = 'Type-safe components for building Sites and Apps with MDX. Production-ready and fully accessible.',
  features = [
    { text: 'Ready-to-use components built with React' },
    { text: 'Fully responsive and accessible by default' },
    { text: 'Easy customization with Tailwind CSS classes' },
  ],
  button,
  learnMore,
  image = { src: '/placeholders/placeholder-1.svg', alt: 'Feature image' },
  mediaType = 'image',
  codeWindow,
  className,
}: FeaturesSplitProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  // contentOnLeft (position='right') = content slides from the left, media from the right.
  const contentOnLeft = position === 'right'
  const contentAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : contentOnLeft
      ? slideFromLeftVariants
      : slideFromRightVariants
  const mediaAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : contentOnLeft
      ? slideFromRightVariants
      : slideFromLeftVariants
  const itemAnimation = prefersReducedMotion ? reducedMotionVariants : listItemVariants

  const content = (
    <motion.div
      variants={contentAnimation}
      className={cn(
        'min-w-0 flex flex-col lg:items-start lg:text-left',
        !contentOnLeft && 'order-1 lg:order-2',
      )}
    >
      <h1 className="my-6 text-2xl sm:text-3xl font-semibold text-pretty">
        {heading}
      </h1>
      <div className="mb-8 max-w-xl text-muted-foreground text-base prose prose-sm max-w-none prose-p:text-muted-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none">
        <Markdown>{description}</Markdown>
      </div>
      <div className="space-y-4">
        {features && features.length > 0 && (
          <ul className="ml-4 space-y-4 text-left">
            {features.map((feature) => (
              <motion.li
                key={feature.text}
                variants={itemAnimation}
                className="flex items-center gap-3"
              >
                <span className="shrink-0 [&_svg]:size-4">{feature.icon ?? <Check className="size-4" />}</span>
                <div className="prose prose-sm max-w-none text-muted-foreground prose-p:m-0 prose-p:text-muted-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none">
                  <Markdown>{feature.text}</Markdown>
                </div>
              </motion.li>
            ))}
          </ul>
        )}
        {(button || learnMore) && (
          <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:items-center sm:gap-4">
            {button && (
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <a href={button.url}>{button.text}</a>
              </Button>
            )}
            {learnMore && (
              <LinkButton
                href={learnMore.url}
                className="w-full justify-center sm:w-auto sm:justify-start"
              >
                {learnMore.text}
              </LinkButton>
            )}
          </div>
        )}
      </div>
    </motion.div>
  )

  const media = (
    <motion.div
      variants={mediaAnimation}
      className={cn(
        'min-w-0 h-auto w-full lg:h-96',
        !contentOnLeft && 'order-2 lg:order-1',
      )}
    >
      {mediaType === 'code' && codeWindow ? (
        <CodeWindowHero
          code={codeWindow.code}
          language={codeWindow.language}
          filename={codeWindow.filename}
          showHeader={false}
          theme="dark"
          size="full"
          className="h-full w-full rounded-[var(--radius-xl,calc(var(--radius)*1.5))]"
        />
      ) : (
        <img
          src={image.src}
          alt={image.alt}
          className="h-full w-full rounded-[var(--radius-xl,calc(var(--radius)*1.5))] object-cover"
        />
      )}
    </motion.div>
  )

  return (
    <motion.section
      ref={sectionRef}
      className={cn('relative overflow-hidden bg-background py-16 sm:py-24 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto">
        <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-12">
          {content}
          {media}
        </div>
      </div>
    </motion.section>
  )
}

export { FeaturesSplit }
